/**
 * Cliente do módulo RECON. Cada função corresponde a uma rota
 * /recon/* na Edge Function `api` (whois, dns, ip, cve, sanctions) —
 * consulta pontual disparada por ação do usuário, nunca por polling do
 * LayerOrchestrator. BTC/ETH ficam fora daqui: são consultadas direto
 * do client em recon/walletLookup.ts (nenhuma das duas fontes exige
 * key, então não há motivo pra round-trip pela Edge Function).
 */

import { apiFetch } from '../lib/apiFetch';

export interface WhoisResult {
  domain: string;
  status: string[];
  nameservers: string[];
  dnssecSigned: boolean | null;
  registrationDate: string | null;
  expirationDate: string | null;
  lastChangedDate: string | null;
  entities: { role: string; name: string | null }[];
}

export async function lookupWhois(domain: string): Promise<WhoisResult> {
  const res = await apiFetch(`/recon/whois?domain=${encodeURIComponent(domain)}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error ?? `Erro ${res.status}`);
  return data as WhoisResult;
}

export interface DnsResult {
  domain: string;
  type: string;
  status: number;
  statusMessage: string | null;
  records: { ttl: number; data: string }[];
}

export async function lookupDns(domain: string, type = 'A'): Promise<DnsResult> {
  const res = await apiFetch(`/recon/dns?domain=${encodeURIComponent(domain)}&type=${encodeURIComponent(type)}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error ?? `Erro ${res.status}`);
  return data as DnsResult;
}

export interface IpIntelResult {
  address: string;
  country: string;
  countryCode: string;
  region: string;
  city: string;
  zip: string;
  lat: number;
  lon: number;
  timezone: string;
  isp: string;
  org: string;
  asn: string;
  asName: string;
  isMobile: boolean;
  isProxy: boolean;
  isHosting: boolean;
}

export async function lookupIp(address: string): Promise<IpIntelResult> {
  const res = await apiFetch(`/recon/ip?address=${encodeURIComponent(address)}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error ?? `Erro ${res.status}`);
  return data as IpIntelResult;
}

export interface CveResult {
  id: string;
  publishedAt: string;
  lastModifiedAt: string;
  status: string;
  description: string | null;
  cvssScore: number | null;
  cvssSeverity: string | null;
  cvssVersion: 'v3.1' | 'v3.0' | 'v2' | null;
  referenceUrls: string[];
}

/** Se `id` for informado, ignora `keyword` — mesma precedência do backend. */
export async function lookupCve(params: { id?: string; keyword?: string }): Promise<CveResult[]> {
  const query = params.id
    ? `id=${encodeURIComponent(params.id)}`
    : `keyword=${encodeURIComponent(params.keyword ?? '')}`;

  const res = await apiFetch(`/recon/cve?${query}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error ?? `Erro ${res.status}`);
  return data.results as CveResult[];
}

export interface SanctionedEntity {
  id: string;
  schema: string;
  caption: string;
  nationality: string[];
  birthDate: string[];
  topics: string[];
  lastChange: string | null;
}

export async function lookupSanctions(query: string): Promise<{ matches: SanctionedEntity[]; truncated: boolean }> {
  const res = await apiFetch(`/recon/sanctions?query=${encodeURIComponent(query)}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error ?? `Erro ${res.status}`);
  return data as { matches: SanctionedEntity[]; truncated: boolean };
}
